// external libraries
import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

// components
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';

// hooks
import useSavePreferences from 'components/flex-portal/FlexMatch/hooks/useSavePreferences';

// stores
import { useRosterPlanStore } from '../RosterPlan.stores';

// types
import type { RootState } from 'redux/rootReducer';
import type { SessionUserPreference } from '../RosterPlan.types';

// utils
import { getRosterPlanColumnsVisibility } from '../utils/getRosterPlanColumnsVisibility';
import { writeSessionUserPreference } from '../utils/sessionUserPreferences';

/**
 * The roster screen's half of the user preferences: it seeds the store from the persisted values,
 * derives which balance columns the grids show, and writes a changed preference back.
 *
 * The seeding runs during the first render rather than from an effect, and that is the whole point
 * of the `useRef` guard. The four store fields replace `useState` initialisers, which apply while
 * the first render is still running; an effect only runs after the first paint, so the grids would
 * paint once without the sort order column and with the default name format before correcting
 * themselves — a visible flicker for exactly those users who set a preference. Writing to a zustand
 * store during render is safe here because the store is a module singleton and
 * `applyStoredPreferences` is a pure derivation of its argument, so running it twice changes
 * nothing.
 *
 * Seeding once per mount is also what reproduces the old behaviour: a `useState` initialiser
 * ignores later changes to its input, so a preference arriving after the first render — the boot
 * saga filling `state.preferences` — must not overwrite what the planner has toggled since.
 */
const useRosterPlanPreferences = () => {
  const userPreferences = useSelector((state: RootState) => state.preferences);
  const period = useDateNavigationStore((state) => state.period);
  const applyStoredPreferences = useRosterPlanStore(
    (state) => state.applyStoredPreferences
  );
  const { mutate: savePreference } = useSavePreferences();

  const hasSeededStore = useRef(false);
  if (!hasSeededStore.current) {
    hasSeededStore.current = true;
    applyStoredPreferences(userPreferences);
  }

  /**
   * Explicit `useMemo` and no React Compiler directive, against the wider repo's habit: this hook's
   * return identity is load bearing (it feeds the counter columns' memo dependency), and the
   * folder keeps an explicit guarantee there rather than the compiler's best effort. Reasoning in
   * README.md, "Decisions".
   */
  const rosterPlanColumnsVisibility = useMemo(
    () =>
      getRosterPlanColumnsVisibility({
        employeeCounters: userPreferences.employeeCounters,
        period
      }),
    [userPreferences.employeeCounters, period]
  );

  /**
   * Fire and forget, which is how all three view mode toggles use it: `useSavePreferences` already
   * reports a failure through its own `onError` snackbar, so `mutate` rather than `mutateAsync`
   * — the latter hands back a promise every call site drops, and a rejected one then surfaces as an
   * unhandled rejection on top of the snackbar the user already saw.
   */
  const saveUserPreferences = ({ key, value }: SessionUserPreference) => {
    writeSessionUserPreference({ key, value });
    savePreference([{ key, value }]);
  };

  return {
    rosterPlanColumnsVisibility,
    dutiesFromOtherCostCenters: userPreferences.dutiesFromOtherCostCenters,
    saveUserPreferences
  };
};

export default useRosterPlanPreferences;
